/**
 * The one guarded path every action takes, from the palette, a bulk bar or a hover card:
 *
 *   low risk                  runs at once
 *   medium, reversible        runs at once, Undo in the toast for 6 s
 *   medium, not reversible    confirm dialog naming the targets
 *   high                      step-up: type the phrase, then any registered step-up provider
 *                             (a passkey, when the security module installs one)
 *
 * Each step is a machine action sent through POST /api/actions, so the hub's policy gate and
 * audit log see it exactly as if it came from a page button. Nothing here can skip policy.
 */
import { create } from 'zustand';
import { ApiError, plural, toast, undoable } from '@orgus/ui';
import type { RiskLevel } from '@orgus/ui';
import { runAction } from '../../lib/api.ts';
import type { ActAction, ActStep } from './catalogue.ts';

export interface StepUpRequest {
  title: string;
  description: string;
  risk: RiskLevel;
  scope: string[];
  /** Typed to unlock the button; null for a plain confirm. */
  phrase: string | null;
  confirmLabel: string;
  run: () => Promise<void>;
}

/**
 * A second factor for high-risk actions (for example a WebAuthn passkey assertion). Resolves
 * true when the person proved presence; false or a throw cancels the action.
 */
export type StepUpProvider = (req: Pick<StepUpRequest, 'title' | 'risk' | 'scope'>) => Promise<boolean>;

interface StepUpStore {
  request: StepUpRequest | null;
  provider: StepUpProvider | null;
}

export const useStepUp = create<StepUpStore>()(() => ({ request: null, provider: null }));

/** Install the step-up provider (the security module calls this once). Returns an uninstall function. */
export function setStepUpProvider(p: StepUpProvider | null): () => void {
  useStepUp.setState({ provider: p });
  return () => {
    if (useStepUp.getState().provider === p) useStepUp.setState({ provider: null });
  };
}

export function requestStepUp(req: StepUpRequest): void {
  useStepUp.setState({ request: req });
}

export function closeStepUp(): void {
  useStepUp.setState({ request: null });
}

/**
 * Run steps one after another; stops at the first refusal and says how far it got, naming the
 * action (`title`, "Remove 5 worktrees") so the message never reads as some other batch.
 */
export async function runSteps(steps: ActStep[], title?: string): Promise<void> {
  let done = 0;
  for (const s of steps) {
    try {
      await runAction(s.machineId, s.kind, s.params as never);
      done++;
    } catch (e) {
      if (done === 0 || !(e instanceof ApiError)) throw e;
      throw new ApiError({
        what: `${title ? `${title}: ` : ''}${done} of ${steps.length} went through, then one failed.`,
        why: e.ui.why,
        next: 'The ones before it went through. Check the list, then try the rest again.',
      });
    }
  }
}

/** The phrase typed to confirm a high-risk action: the verb and the count ("stop 3 sessions"). */
export function phraseFor(a: Pick<ActAction, 'verb' | 'scopeLabel'>): string {
  const count = /^(\d+ [a-z]+)/.exec(a.scopeLabel)?.[1] ?? a.scopeLabel;
  return `${a.verb.toLowerCase()} ${count}`;
}

/** Which route an action takes, by risk and reversibility. */
export function guardFor(a: Pick<ActAction, 'risk' | 'reversible'>): 'run' | 'undo' | 'confirm' | 'step-up' {
  if (a.risk === 'high') return 'step-up';
  if (a.risk === 'medium') return a.reversible ? 'undo' : 'confirm';
  return a.reversible ? 'undo' : 'run';
}

async function execute(a: ActAction): Promise<void> {
  if (a.custom) await a.custom();
  else await runSteps(a.steps, a.title);
}

/** Take an action through its guard. Resolves once it ran, or once the confirm opened. */
export async function performAct(a: ActAction): Promise<void> {
  const route = guardFor(a);
  if (route === 'run' || route === 'undo') {
    const id = toast.working(a.title, 'Waiting for the machine to answer.');
    try {
      await execute(a);
      toast.dismiss(id);
      if (route === 'undo' && a.undo) {
        const undo = a.undo;
        undoable({ title: a.done, description: a.scopeLabel, undo: () => runSteps(undo, `Undo ${a.title}`), undoneTitle: 'Undone' });
      } else {
        toast.success(a.done, a.scopeLabel);
      }
    } catch (e) {
      toast.error(e, id);
    }
    return;
  }
  requestStepUp({
    title: `${a.title}?`,
    description: a.consequence,
    risk: a.risk,
    scope: a.scope,
    phrase: route === 'step-up' ? phraseFor(a) : null,
    confirmLabel: a.steps.length > 1 ? `${a.verb} ${plural(a.steps.length, a.scopeLabel.split(' ')[1]?.replace(/s$/, '') ?? 'item')}` : a.verb,
    run: async () => {
      const provider = useStepUp.getState().provider;
      if (route === 'step-up' && provider) {
        const ok = await provider({ title: a.title, risk: a.risk, scope: a.scope });
        if (!ok) throw new ApiError({ what: 'The action did not run.', why: 'The extra sign-in step was not completed.', next: 'Try again and finish the prompt from your device.' });
      }
      await execute(a);
      toast.success(a.done, a.scopeLabel);
    },
  });
}
